import {
  GoogleMap,
  InfoWindow,
  Marker,
  Polyline,
} from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FiEye } from "react-icons/fi";
import { socket } from "../../utils/socket";

const SchoolBusTracker = ({ trip, currentUserId, currentUserRole }) => {
  // Extract data from API response
  const route = trip.route || {};
  const driver = trip.driver || {};
  const students = trip.students || [];
  const school = trip.school || {};

  // Route direction flags
  const isBusingSubscription = trip.booking_type === "busing_subscription";
  const tripLeg = trip.trip_leg || null; // "outgoing" | "return" | null

  // Afternoon leg = School → Home (drop-off run)
  const isAfternoonLeg =
    isBusingSubscription &&
    (trip.subscription_type === "afternoon" ||
      (trip.subscription_type === "morning_afternoon" && tripLeg === "return"));

  // School's actual position
  const schoolPos = useMemo(() => {
    const sLat = parseFloat(school.school_location?.latitude);
    const sLng = parseFloat(school.school_location?.longitude);
    if (sLat && sLng) return { lat: sLat, lng: sLng };
    if (isBusingSubscription) {
      return {
        lat: parseFloat(route.dropoff_lat) || 0,
        lng: parseFloat(route.dropoff_lng) || 0,
      };
    }
    return {
      lat: parseFloat(trip.end_location?.latitude) || 0,
      lng: parseFloat(trip.end_location?.longitude) || 0,
    };
  }, [
    school.school_location,
    isBusingSubscription,
    route.dropoff_lat,
    route.dropoff_lng,
    trip.end_location,
  ]);

  // Route start:
  //   Morning  (Home → School): start at route pickup area (student area)
  //   Afternoon(School → Home): start at school
  //   Bus charter: start at trip.start_location
  const routeStart = useMemo(() => {
    if (isBusingSubscription) {
      if (isAfternoonLeg) {
        // Afternoon: start from school
        return schoolPos;
      }
      // Morning: start from route pickup area
      return {
        lat: parseFloat(route.pickup_lat) || 0,
        lng: parseFloat(route.pickup_lng) || 0,
      };
    }
    return {
      lat: parseFloat(trip.start_location?.latitude) || 0,
      lng: parseFloat(trip.start_location?.longitude) || 0,
    };
  }, [
    isBusingSubscription,
    isAfternoonLeg,
    schoolPos,
    route.pickup_lat,
    route.pickup_lng,
    trip.start_location,
  ]);

  // Route end:
  //   Morning  (Home → School): end at school
  //   Afternoon(School → Home): end at route dropoff area (student area)
  //   Bus charter: end at trip.end_location
  const routeEnd = useMemo(() => {
    if (isBusingSubscription) {
      if (isAfternoonLeg) {
        // Afternoon: end at route dropoff area
        return {
          lat: parseFloat(route.dropoff_lat) || 0,
          lng: parseFloat(route.dropoff_lng) || 0,
        };
      }
      // Morning: end at school
      return schoolPos;
    }
    return {
      lat: parseFloat(trip.end_location?.latitude) || 0,
      lng: parseFloat(trip.end_location?.longitude) || 0,
    };
  }, [
    isBusingSubscription,
    isAfternoonLeg,
    schoolPos,
    route.dropoff_lat,
    route.dropoff_lng,
    trip.end_location,
  ]);

  // Driver's current location from API (initial), updated via socket
  // If driver location is too far from route (>50km), fallback to route start
  const [driverPos, setDriverPos] = useState(() => {
    const dLat = parseFloat(driver.current_location?.latitude);
    const dLng = parseFloat(driver.current_location?.longitude);
    if (dLat && dLng) {
      const dist = Math.sqrt(
        Math.pow(dLat - routeStart.lat, 2) + Math.pow(dLng - routeStart.lng, 2),
      );
      // ~0.45 degrees ≈ 50km, if driver is way off use route start
      if (dist < 0.45) return { lat: dLat, lng: dLng };
    }
    return { lat: routeStart.lat, lng: routeStart.lng };
  });

  const animationRef = useRef(null);
  const lastUpdateTimeRef = useRef(performance.now());

  // Smoothly animate bus marker from current pos to new pos
  const animateToPosition = useCallback((toPos) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    // Duration = exactly match GPS update interval for continuous smooth movement
    const now = performance.now();
    const gap = now - lastUpdateTimeRef.current;
    lastUpdateTimeRef.current = now;
    // Use actual gap clamped to reasonable range — fills exactly to next update
    const duration = Math.min(Math.max(gap, 800), 2000);

    setDriverPos((fromPos) => {
      const from = { ...fromPos };
      const startTime = performance.now();

      const step = (timestamp) => {
        const elapsed = timestamp - startTime;
        const progress = Math.min(elapsed / duration, 1);
        // Linear easing for continuous smooth motion (no acceleration/deceleration)
        const eased = progress;

        const lat = from.lat + (toPos.lat - from.lat) * eased;
        const lng = from.lng + (toPos.lng - from.lng) * eased;
        setDriverPos({ lat, lng });

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(step);
        }
      };

      animationRef.current = requestAnimationFrame(step);
      return from;
    });
  }, []);

  const [routePath, setRoutePath] = useState([]);

  // Project point onto segment a→b, return interpolated point + t (0-1)
  const projectOnSegment = (p, a, b) => {
    const ax = b.lat - a.lat;
    const ay = b.lng - a.lng;
    const len2 = ax * ax + ay * ay;
    if (len2 < 1e-20) return { pt: a, t: 0 };
    const t = Math.max(
      0,
      Math.min(1, ((p.lat - a.lat) * ax + (p.lng - a.lng) * ay) / len2),
    );
    return { pt: { lat: a.lat + t * ax, lng: a.lng + t * ay }, t };
  };

  // Snap bus to nearest point on route
  const snappedPos = useMemo(() => {
    if (!routePath.length) return driverPos;
    let minDist = Infinity;
    let nearest = routePath[0];
    for (let i = 0; i < routePath.length - 1; i++) {
      const { pt } = projectOnSegment(
        driverPos,
        routePath[i],
        routePath[i + 1],
      );
      const d =
        Math.pow(pt.lat - driverPos.lat, 2) +
        Math.pow(pt.lng - driverPos.lng, 2);
      if (d < minDist) {
        minDist = d;
        nearest = pt;
      }
    }
    return nearest;
  }, [driverPos, routePath]);

  // Trimmed blue route — smooth continuous trim as bus moves
  const trimmedRoute = useMemo(() => {
    if (!routePath.length || !driverPos?.lat) return routePath;
    let bestDist = Infinity;
    let bestSeg = 0;
    let bestPt = routePath[0];

    for (let i = 0; i < routePath.length - 1; i++) {
      const { pt } = projectOnSegment(
        driverPos,
        routePath[i],
        routePath[i + 1],
      );
      const d =
        Math.pow(pt.lat - driverPos.lat, 2) +
        Math.pow(pt.lng - driverPos.lng, 2);
      if (d < bestDist) {
        bestDist = d;
        bestSeg = i;
        bestPt = pt;
      }
    }

    // Start from exact projected point on segment → smooth, no jumps
    return [bestPt, ...routePath.slice(bestSeg + 1)];
  }, [routePath, driverPos]);
  const [mapCenter, setMapCenter] = useState(routeStart);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showDriverInfo, setShowDriverInfo] = useState(false);
  const [showDropoffInfo, setShowDropoffInfo] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [studentStatuses, setStudentStatuses] = useState(() =>
    students.map((s) => ({
      ...s,
      displayStatus: s.attendance?.status || "scheduled",
    })),
  );

  // Trip status from API, updated via socket
  const [tripStatus, setTripStatus] = useState(trip.status || "started");

  // ETA — next stop + full route, from Google Directions legs
  const [eta, setEta] = useState({ nextStopMinutes: null, totalMinutes: null });

  // Build student stop markers from real data
  // Morning: use pickup_location (student's home as pickup)
  // Afternoon: use dropoff_location (student's home as dropoff)
  const studentStops = useMemo(
    () =>
      students
        .map((s) => {
          const loc = isAfternoonLeg ? s.dropoff_location : s.pickup_location;
          if (!loc?.latitude || !loc?.longitude) return null;
          return {
            student_id: s.student_id,
            student_name: s.student_name,
            lat: parseFloat(loc.latitude),
            lng: parseFloat(loc.longitude),
            address: loc.address || "",
            attendance_status: s.attendance?.status || "scheduled",
            parent_id: s.parent?.parent_id || null,
            parent_name: s.parent?.parent_name || "",
            parent_phone: s.parent?.parent_phone || "",
          };
        })
        .filter(Boolean),
    [students, isAfternoonLeg],
  );

  // Fetch driving route from Google Directions API
  // Origin = driver's current position so the blue line starts from the bus icon
  useEffect(() => {
    if (!window.google) return;
    if (!driverPos?.lat || !driverPos?.lng) return;

    // Trip is over — stop drawing/updating the route line
    if (tripStatus === "completed" || tripStatus === "cancelled") {
      setRoutePath([]);
      setEta({ nextStopMinutes: null, totalMinutes: null });
      return;
    }

    const fetchRoute = () => {
      const directionsService = new window.google.maps.DirectionsService();

      const getStatus = (stop) => {
        const statusObj = studentStatuses.find(
          (s) => s.student_id === stop.student_id,
        );
        return statusObj?.displayStatus || stop.attendance_status;
      };

      // Stops still pending:
      //   Outgoing (morning): not yet picked up from home ("scheduled")
      //   Return (afternoon): not yet dropped off at home — "scheduled" (still
      //     waiting at school) or "picked_up" (on the bus, awaiting dropoff)
      const pendingStops = studentStops.filter((stop) => {
        const status = getStatus(stop);
        return isAfternoonLeg
          ? status === "scheduled" || status === "picked_up"
          : status === "scheduled";
      });

      // Return trip: if any student hasn't been picked up from school yet,
      // the driver must visit the school FIRST before dropping anyone home.
      const needsSchoolStopFirst =
        isAfternoonLeg &&
        studentStops.some((stop) => getStatus(stop) === "scheduled");

      let destination = routeEnd;
      let waypointStops = pendingStops;

      if (isAfternoonLeg && pendingStops.length > 0) {
        // Last pending student's home is the actual destination
        destination = {
          lat: pendingStops[pendingStops.length - 1].lat,
          lng: pendingStops[pendingStops.length - 1].lng,
        };
        waypointStops = pendingStops.slice(0, -1);
      }

      const waypoints = waypointStops.map((stop) => ({
        location: { lat: stop.lat, lng: stop.lng },
        stopover: true,
      }));

      if (needsSchoolStopFirst) {
        waypoints.unshift({ location: schoolPos, stopover: true });
      }

      const request = {
        origin: { lat: driverPos.lat, lng: driverPos.lng },
        destination,
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
        optimizeWaypoints: false,
      };

      directionsService.route(request, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const path = result.routes[0].overview_path.map((point) => ({
            lat: point.lat(),
            lng: point.lng(),
          }));
          setRoutePath(path);

          // ETA — leg[0].duration = time to next stop, sum of all legs = total time
          const legs = result.routes[0].legs || [];
          const nextSeconds = legs[0]?.duration?.value ?? null;
          const totalSeconds = legs.reduce(
            (sum, leg) => sum + (leg.duration?.value || 0),
            0,
          );
          setEta({
            nextStopMinutes: nextSeconds != null ? Math.max(1, Math.round(nextSeconds / 60)) : null,
            totalMinutes: totalSeconds ? Math.max(1, Math.round(totalSeconds / 60)) : null,
          });
        }
      });
    };

    const timer = setTimeout(fetchRoute, 500);
    return () => clearTimeout(timer);
  }, [driverPos, routeEnd, schoolPos, isAfternoonLeg, studentStops, studentStatuses, tripStatus]);

  // Socket.IO: Subscribe to real-time driver location updates
  useEffect(() => {
    const reservationId = trip.reservation_id;
    if (!reservationId) return;

    const subscribe = () => {
      socket.emit("subscribe_tracking", { reservationId });
    };

    // Subscribe immediately if already connected, otherwise wait for connect event
    if (socket.connected) {
      subscribe();
    } else {
      socket.once("connect", subscribe);
    }

    // Listen for driver location updates — animate smoothly
    const handleLocationUpdate = (data) => {
      if (data.reservationId === reservationId && data.location) {
        animateToPosition({
          lat: parseFloat(data.location.latitude),
          lng: parseFloat(data.location.longitude),
        });
      }
    };

    // Listen for student pickup/dropoff status changes
    const handleStudentStatus = (data) => {
      if (data.reservationId === reservationId) {
        setStudentStatuses((prev) =>
          prev.map((s) =>
            s.student_id === data.studentId
              ? { ...s, displayStatus: data.status }
              : s,
          ),
        );
      }
    };

    // Listen for initial tracking data when subscribing
    const handleTrackingSubscribed = (data) => {
      if (
        data.reservationId === reservationId &&
        data.currentLocation?.location
      ) {
        animateToPosition({
          lat: parseFloat(data.currentLocation.location.latitude),
          lng: parseFloat(data.currentLocation.location.longitude),
        });
      }
    };

    // Listen for trip status changes (started, in_progress, completed)
    const handleTripStatus = (data) => {
      if (data.reservationId === reservationId) {
        setTripStatus(data.status);
      }
    };

    socket.on("driver_location_updated", handleLocationUpdate);
    socket.on("student_status_changed", handleStudentStatus);
    socket.on("tracking_subscribed", handleTrackingSubscribed);
    socket.on("trip_status_changed", handleTripStatus);

    return () => {
      socket.off("connect", subscribe);
      socket.off("driver_location_updated", handleLocationUpdate);
      socket.off("student_status_changed", handleStudentStatus);
      socket.off("tracking_subscribed", handleTrackingSubscribed);
      socket.off("trip_status_changed", handleTripStatus);
      if (socket.connected) {
        socket.emit("unsubscribe_tracking", { reservationId });
      }
      // Cancel any ongoing animation
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [trip.reservation_id, animateToPosition]);

  // Recenter map on driver
  const handleRecenter = useCallback(() => {
    setMapCenter(driverPos);
  }, [driverPos]);

  // Get attendance status display
  // On the return (afternoon) leg, "picked_up" means the student is on the
  // bus and still awaiting dropoff at home — not yet "done".
  const getAttendanceLabel = (status) => {
    switch (status) {
      case "picked_up":
        return isAfternoonLeg ? "On Bus" : "Picked Up";
      case "dropped_off":
        return "Dropped Off";
      case "cancelled":
        return "Cancelled";
      case "absent":
        return "Absent";
      default:
        return isAfternoonLeg ? "Waiting at School" : "Scheduled";
    }
  };

  const getAttendanceColor = (status) => {
    switch (status) {
      case "picked_up":
        return isAfternoonLeg ? "#FFA500" : "#4CAF50";
      case "dropped_off":
        return "#2196F3";
      case "cancelled":
      case "absent":
        return "#f44336";
      default:
        return "#FFA500";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "started":
      case "in_progress":
        return "#4CAF50";
      case "completed":
        return "#2196F3";
      default:
        return "#FFA500";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "started":
        return "En Route";
      case "in_progress":
        return "In Progress";
      case "completed":
        return "Completed";
      default:
        return "Starting";
    }
  };

  const getSubscriptionTypeLabel = (type) => {
    switch (type) {
      case "morning_afternoon":
        return "Morning & Afternoon";
      case "morning":
        return "Morning";
      case "afternoon":
        return "Afternoon";
      default:
        return type || "";
    }
  };

  // Icons - must use google.maps.Size/Point for proper rendering
  const gm = window.google?.maps;

  const busIcon = useMemo(() => {
    if (!gm) return undefined;
    return {
      url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='45' height='45' viewBox='0 0 24 24' fill='%23FF6B35'%3E%3Cpath d='M4 16c0 .88.39 1.67 1 2.22V20c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h8v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1.78c.61-.55 1-1.34 1-2.22V6c0-3.5-3.58-4-8-4s-8 .5-8 4v10zm3.5 1c-.83 0-1.5-.67-1.5-1.5S6.67 14 7.5 14s1.5.67 1.5 1.5S8.33 17 7.5 17zm9 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm1.5-6H6V6h12v5z'/%3E%3C/svg%3E",
      scaledSize: new gm.Size(45, 45),
      anchor: new gm.Point(22, 22),
    };
  }, [gm]);

  const schoolIcon = useMemo(() => {
    if (!gm) return undefined;
    return {
      url: "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='40' viewBox='0 0 24 24' fill='%231565C0'%3E%3Cpath d='M5 13.18v4L12 21l7-3.82v-4L12 17l-7-3.82zM12 3L1 9l11 6 9-4.91V17h2V9L12 3z'/%3E%3C/svg%3E",
      scaledSize: new gm.Size(40, 40),
      anchor: new gm.Point(20, 40),
    };
  }, [gm]);

  const getStopIcon = useCallback(
    (status, stopNumber) => {
      if (!gm) return undefined;
      let bgColor = "%23FF9800"; // orange
      let textColor = "%23ffffff";
      if (status === "picked_up" || status === "dropped_off")
        bgColor = "%234CAF50"; // green
      else if (status === "cancelled" || status === "absent")
        bgColor = "%23f44336"; // red
      const num = stopNumber ?? "";
      const svg = `%3Csvg xmlns='http://www.w3.org/2000/svg' width='36' height='44' viewBox='0 0 36 44'%3E%3Cellipse cx='18' cy='18' rx='16' ry='16' fill='${bgColor}' stroke='white' stroke-width='2'/%3E%3Ctext x='18' y='23' text-anchor='middle' font-size='14' font-weight='bold' font-family='Arial' fill='${textColor}'%3E${num}%3C/text%3E%3Cpolygon points='18,44 11,30 25,30' fill='${bgColor}'/%3E%3C/svg%3E`;
      return {
        url: `data:image/svg+xml,${svg}`,
        scaledSize: new gm.Size(36, 44),
        anchor: new gm.Point(18, 44),
      };
    },
    [gm],
  );

  // Merged student statuses for display
  const mergedStudents = useMemo(
    () =>
      studentStops.map((stop) => {
        const statusObj = studentStatuses.find(
          (s) => s.student_id === stop.student_id,
        );
        return {
          ...stop,
          displayStatus: statusObj?.displayStatus || stop.attendance_status,
        };
      }),
    [studentStops, studentStatuses],
  );

  // Group students by location — siblings share same lat/lng → one marker, multiple kids
  const groupedStops = useMemo(() => {
    const groups = [];
    const keyMap = {};
    mergedStudents.forEach((stop, index) => {
      // Round to 4 decimal places (~11m precision) to group same-location students
      const key = `${stop.lat.toFixed(4)}_${stop.lng.toFixed(4)}`;
      if (keyMap[key] !== undefined) {
        groups[keyMap[key]].students.push(stop);
      } else {
        keyMap[key] = groups.length;
        groups.push({
          key,
          lat: stop.lat,
          lng: stop.lng,
          address: stop.address,
          stopNumber: groups.length + 1,
          students: [stop],
        });
      }
    });
    return groups;
  }, [mergedStudents]);

  // Render map content (shared between inline and modal)
  const renderMapMarkers = () => (
    <>
      {/* Route polyline — trimmed behind driver */}
      {trimmedRoute.length > 1 && (
        <Polyline
          path={trimmedRoute}
          options={{
            strokeColor: "#2196F3",
            strokeOpacity: 0.8,
            strokeWeight: 5,
          }}
        />
      )}

      {/* Red dotted line: bus GPS → nearest road point (only when off-road > 20m) */}
      {(() => {
        if (!routePath.length || !driverPos?.lat) return null;
        const dLat = (driverPos.lat - snappedPos.lat) * 111000;
        const dLng =
          (driverPos.lng - snappedPos.lng) *
          111000 *
          Math.cos((driverPos.lat * Math.PI) / 180);
        const distMeters = Math.sqrt(dLat * dLat + dLng * dLng);
        if (distMeters < 15) return null;
        return (
          <Polyline
            path={[driverPos, snappedPos]}
            options={{
              strokeColor: "#FF3B30",
              strokeOpacity: 0,
              strokeWeight: 0,
              icons: [
                {
                  icon: {
                    path: "M 0,-1 0,1",
                    strokeOpacity: 1,
                    strokeColor: "#FF3B30",
                    strokeWeight: 2.5,
                    scale: 4,
                  },
                  offset: "0",
                  repeat: "14px",
                },
              ],
            }}
          />
        );
      })()}

      {/* School marker at school's actual location */}
      <Marker
        position={schoolPos}
        icon={schoolIcon}
        onClick={() => setShowDropoffInfo(!showDropoffInfo)}
      >
        {showDropoffInfo && (
          <InfoWindow onCloseClick={() => setShowDropoffInfo(false)}>
            <div>
              <div style={{ fontWeight: "600" }}>
                {/* {school?.school_name || "School"} */}
                {school?.school_company_name || "School"}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {school?.school_email || ""}
              </div>
              <div
                style={{ fontSize: "11px", color: "#888", marginTop: "3px" }}
              >
                {school?.school_phone || ""}
              </div>
            </div>
          </InfoWindow>
        )}
      </Marker>

      {/* Student pickup markers — grouped by location */}
      {groupedStops.map((group) => {
        // On the return (afternoon) leg, "picked_up" means the student is on
        // the bus and still awaiting dropoff — treat it as pending, not done.
        const hasScheduled = group.students.some((s) =>
          isAfternoonLeg
            ? s.displayStatus === "scheduled" || s.displayStatus === "picked_up"
            : s.displayStatus === "scheduled",
        );
        const hasAbsent = group.students.some(
          (s) =>
            s.displayStatus === "cancelled" || s.displayStatus === "absent",
        );
        const allDone = group.students.every((s) =>
          isAfternoonLeg
            ? s.displayStatus === "dropped_off"
            : s.displayStatus === "picked_up" || s.displayStatus === "dropped_off",
        );
        const groupStatus = hasScheduled
          ? "scheduled"
          : hasAbsent
            ? "absent"
            : allDone
              ? "picked_up"
              : "scheduled";

        // Check if this stop belongs to the logged-in parent
        const isMyStop =
          currentUserRole === "parent" &&
          currentUserId &&
          group.students.some((s) => s.parent_id === currentUserId);

        // Build icon — parent's own stop gets a special "MY STOP" star marker
        const stopIcon = isMyStop
          ? (() => {
              if (!gm) return undefined;
              const stopNum = group.stopNumber;
              const label = encodeURIComponent(`${stopNum} MY STOP`);
              const svg = `%3Csvg xmlns='http://www.w3.org/2000/svg' width='90' height='44' viewBox='0 0 90 44'%3E%3Crect x='1' y='1' width='88' height='30' rx='8' ry='8' fill='%234CAF50' stroke='white' stroke-width='2.5'/%3E%3Ctext x='45' y='21' text-anchor='middle' font-size='11' font-weight='bold' font-family='Arial' fill='white'%3E${stopNum} MY STOP%3C/text%3E%3Cpolygon points='45,44 36,32 54,32' fill='%234CAF50'/%3E%3C/svg%3E`;
              return {
                url: `data:image/svg+xml,${svg}`,
                scaledSize: new gm.Size(90, 44),
                anchor: new gm.Point(45, 44),
              };
            })()
          : getStopIcon(groupStatus, group.stopNumber);

        return (
          <Marker
            key={group.key}
            position={{ lat: group.lat, lng: group.lng }}
            icon={stopIcon}
            zIndex={isMyStop ? 999 : undefined}
            onClick={() => setSelectedStudent(group.key)}
          >
            {selectedStudent === group.key && (
              <InfoWindow onCloseClick={() => setSelectedStudent(null)}>
                <div style={{ minWidth: "180px" }}>
                  <div
                    style={{
                      fontWeight: "700",
                      fontSize: "13px",
                      color: "#FF9800",
                      marginBottom: "4px",
                    }}
                  >
                    Stop #{group.stopNumber} &nbsp;·&nbsp;{" "}
                    {group.students.length} student
                    {group.students.length > 1 ? "s" : ""}
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginBottom: "8px",
                    }}
                  >
                    {group.address}
                  </div>
                  {group.students.map((s) => (
                    <div
                      key={s.student_id}
                      style={{
                        paddingBottom: "8px",
                        marginBottom: "8px",
                        borderBottom:
                          group.students.length > 1 ? "1px solid #eee" : "none",
                      }}
                    >
                      <div style={{ fontWeight: "600", fontSize: "13px" }}>
                        {s.student_name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#888" }}>
                        Parent: {s.parent_name}
                      </div>
                      <div
                        style={{
                          fontSize: "11px",
                          marginTop: "3px",
                          color: getAttendanceColor(s.displayStatus),
                          fontWeight: "600",
                        }}
                      >
                        {getAttendanceLabel(s.displayStatus)}
                      </div>
                    </div>
                  ))}
                </div>
              </InfoWindow>
            )}
          </Marker>
        );
      })}

      {/* Driver / Bus marker */}
      <Marker
        position={driverPos}
        icon={busIcon}
        onClick={() => setShowDriverInfo(!showDriverInfo)}
      >
        {showDriverInfo && (
          <InfoWindow onCloseClick={() => setShowDriverInfo(false)}>
            <div>
              <div style={{ fontWeight: "600" }}>
                {driver.driver_name || "Driver"}
              </div>
              <div style={{ fontSize: "12px", color: "#666" }}>
                {driver.driver_phone || ""}
              </div>
              <div
                style={{ fontSize: "11px", marginTop: "3px", color: "#888" }}
              >
                {getSubscriptionTypeLabel(trip.subscription_type) ||
                  trip.booking_type}
              </div>
            </div>
          </InfoWindow>
        )}
      </Marker>
    </>
  );

  const pickedCount = mergedStudents.filter(
    (s) => s.displayStatus === "picked_up" || s.displayStatus === "dropped_off",
  ).length;

  const droppedCount = mergedStudents.filter(
    (s) => s.displayStatus === "dropped_off",
  ).length;

  const pendingCount = mergedStudents.filter(
    (s) => s.displayStatus === "scheduled",
  ).length;

  const absentCount = mergedStudents.filter(
    (s) => s.displayStatus === "absent" || s.displayStatus === "cancelled",
  ).length;

  return (
    <div style={{ width: "100%", height: "100%", position: "relative" }}>
      {/* Map Legend */}
      {/* <div
        style={{
          position: "absolute",
          bottom: "30px",
          right: "10px",
          zIndex: 1000,
          backgroundColor: "white",
          padding: "8px 12px",
          borderRadius: "8px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          fontSize: "11px",
          color: "#555",
          lineHeight: "1.8",
        }}
      >
        <div style={{ fontWeight: "700", marginBottom: "4px", color: "#333" }}>Map Legend</div>
        <div>🔢 <strong>Number</strong> = Stop order (1st, 2nd…)</div>
        <div>🟠 Scheduled &nbsp; 🟢 Done &nbsp; 🔴 Absent</div>
      </div> */}

      {/* Status Bar */}
      <div
        style={{
          position: "absolute",
          top: "10px",
          left: "10px",
          right: "10px",
          zIndex: 1000,
          backgroundColor: "white",
          padding: "10px",
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "12px",
                height: "12px",
                borderRadius: "50%",
                backgroundColor: getStatusColor(tripStatus),
                animation: "pulse 2s infinite",
              }}
            ></div>
            <div>
              <span style={{ fontWeight: "bold", fontSize: "13px" }}>
                {route.route_name || "Trip"} - {getStatusLabel(tripStatus)}
              </span>
              <div style={{ fontSize: "11px", color: "#888" }}>
                {driver.driver_name} &bull;{" "}
                {getSubscriptionTypeLabel(trip.subscription_type) ||
                  trip.booking_type}
                {tripLeg && (
                  <span
                    style={{
                      marginLeft: "6px",
                      padding: "1px 6px",
                      borderRadius: "8px",
                      backgroundColor:
                        tripLeg === "outgoing" ? "#e3f2fd" : "#f3e5f5",
                      color: tripLeg === "outgoing" ? "#1565c0" : "#6a1b9a",
                      fontWeight: "600",
                      fontSize: "10px",
                      textTransform: "capitalize",
                    }}
                  >
                    {tripLeg}
                  </span>
                )}
                {eta.nextStopMinutes != null && (
                  <span
                    style={{
                      marginLeft: "6px",
                      padding: "1px 6px",
                      borderRadius: "8px",
                      backgroundColor: "#fff3e0",
                      color: "#e65100",
                      fontWeight: "700",
                      fontSize: "10px",
                    }}
                  >
                    ETA {eta.nextStopMinutes} min
                  </span>
                )}
              </div>
            </div>
          </div>
          <button
            onClick={() => setOpenModal(true)}
            style={{
              padding: "8px 16px",
              backgroundColor: "#1e293b",
              color: "white",
              borderRadius: "6px",
              border: "none",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: "600",
              fontSize: "13px",
            }}
          >
            <FiEye />
          </button>
        </div>
      </div>

      {/* ==================== MODAL ==================== */}
      <Modal
        show={openModal}
        onHide={() => setOpenModal(false)}
        className="tMap"
      >
        <Modal.Header closeButton style={{ backgroundColor: "#1e293b" }}>
          <Modal.Title style={{ width: "100%" }}>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                width: "100%",
                flexWrap: "wrap",
                gap: "16px",
              }}
            >
              {/* Left: Status */}
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "12px",
                  flexGrow: 1,
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <div
                    style={{
                      position: "relative",
                      width: "16px",
                      height: "16px",
                    }}
                  >
                    <div
                      style={{
                        width: "100%",
                        height: "100%",
                        borderRadius: "50%",
                        backgroundColor: getStatusColor(tripStatus),
                        animation: "pulse 2s infinite",
                      }}
                    ></div>
                    <div
                      style={{
                        position: "absolute",
                        top: "-4px",
                        left: "-4px",
                        right: "-4px",
                        bottom: "-4px",
                        borderRadius: "50%",
                        border: `2px solid ${getStatusColor(tripStatus)}`,
                        opacity: 0.4,
                        animation: "ripple 2s infinite",
                      }}
                    ></div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span
                      style={{
                        fontWeight: "600",
                        fontSize: "14px",
                        color: "rgba(255, 255, 255, 0.7)",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {route.route_name || "TRIP"} -{" "}
                      {driver.driver_name || "DRIVER"}
                    </span>
                    <span
                      style={{
                        fontWeight: "bold",
                        fontSize: "18px",
                        color: "white",
                        lineHeight: "1.2",
                      }}
                    >
                      {getStatusLabel(tripStatus)} &bull; Live Tracking Active
                      {tripLeg && (
                        <span
                          style={{
                            marginLeft: "8px",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            backgroundColor:
                              tripLeg === "outgoing" ? "#1976d2" : "#7b1fa2",
                            color: "white",
                            fontWeight: "600",
                            fontSize: "11px",
                            textTransform: "capitalize",
                            verticalAlign: "middle",
                          }}
                        >
                          {tripLeg}
                        </span>
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Stats Cards */}
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  flexWrap: "wrap",
                  flexGrow: 1,
                }}
              >
                {/* Students Card */}
                <div
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    minWidth: "120px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(0, 0, 0, 0.05)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                    }}
                  >
                    Students:{" "}
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "800",
                        color: "#1a2b3c",
                      }}
                    >
                      {mergedStudents.length}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#888",
                      marginTop: "4px",
                    }}
                  >
                    Total on route
                  </div>
                </div>

                {/* Pending Pickups Card */}
                <div
                  style={{
                    backgroundColor: "#fff9f0",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    minWidth: "120px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(255, 152, 0, 0.15)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#e65100",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                    }}
                  >
                    Pending:{" "}
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "800",
                        color: "#FF9800",
                      }}
                    >
                      {pendingCount}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#e65100",
                      marginTop: "4px",
                    }}
                  >
                    Awaiting pickup
                  </div>
                </div>

                {/* Picked Up Card */}
                <div
                  style={{
                    backgroundColor: "#f1f8f4",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    minWidth: "120px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(76, 175, 80, 0.15)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#2e7d32",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                    }}
                  >
                    Picked:{" "}
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "800",
                        color: "#4CAF50",
                      }}
                    >
                      {pickedCount - droppedCount}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#2e7d32",
                      marginTop: "4px",
                    }}
                  >
                    On board
                  </div>
                </div>

                {/* Dropped Off Card */}
                <div
                  style={{
                    backgroundColor: "#e8f4fd",
                    padding: "10px 20px",
                    borderRadius: "12px",
                    textAlign: "center",
                    minWidth: "120px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                    border: "1px solid rgba(33, 150, 243, 0.15)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#1565c0",
                      marginBottom: "6px",
                      fontWeight: "600",
                      textTransform: "uppercase",
                    }}
                  >
                    Dropped:{" "}
                    <span
                      style={{
                        fontSize: "24px",
                        fontWeight: "800",
                        color: "#2196F3",
                      }}
                    >
                      {droppedCount}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: "11px",
                      color: "#1565c0",
                      marginTop: "4px",
                    }}
                  >
                    Dropped off
                  </div>
                </div>

                {/* ETA Card — next stop + total remaining time */}
                {(eta.nextStopMinutes != null || eta.totalMinutes != null) && (
                  <div
                    style={{
                      backgroundColor: "#fff3e0",
                      padding: "10px 20px",
                      borderRadius: "12px",
                      textAlign: "center",
                      minWidth: "120px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                      border: "1px solid rgba(230, 81, 0, 0.15)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#e65100",
                        marginBottom: "6px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      ETA:{" "}
                      <span
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: "#e65100",
                        }}
                      >
                        {eta.nextStopMinutes ?? "--"}
                      </span>{" "}
                      min
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#e65100",
                        marginTop: "4px",
                      }}
                    >
                      Next stop
                      {eta.totalMinutes != null && (
                        <> &bull; {eta.totalMinutes} min total</>
                      )}
                    </div>
                  </div>
                )}

                {/* Absent Card — only show if there are absents */}
                {absentCount > 0 && (
                  <div
                    style={{
                      backgroundColor: "#fff5f5",
                      padding: "10px 20px",
                      borderRadius: "12px",
                      textAlign: "center",
                      minWidth: "120px",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
                      border: "1px solid rgba(244, 67, 54, 0.15)",
                    }}
                  >
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#c62828",
                        marginBottom: "6px",
                        fontWeight: "600",
                        textTransform: "uppercase",
                      }}
                    >
                      Absent:{" "}
                      <span
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: "#f44336",
                        }}
                      >
                        {absentCount}
                      </span>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        color: "#c62828",
                        marginTop: "4px",
                      }}
                    >
                      Not on route
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div style={{ width: "100%", height: "83vh", position: "relative" }}>
            {/* Stops Sidebar */}
            <div
              style={{
                position: "absolute",
                bottom: "10px",
                left: "10px",
                zIndex: 1000,
                backgroundColor: "white",
                padding: "15px",
                borderRadius: "12px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                width: "280px",
                maxHeight: "50vh",
                overflowY: "auto",
              }}
            >
              <h6
                style={{
                  margin: "0 0 15px 0",
                  fontWeight: "bold",
                  fontSize: "16px",
                  paddingBottom: "10px",
                  borderBottom: "2px solid #f0f0f0",
                }}
              >
                Students ({pickedCount}/{mergedStudents.length})
              </h6>
              {mergedStudents.map((stop) => (
                <div
                  key={stop.student_id}
                  style={{
                    padding: "12px",
                    borderLeft: `4px solid ${getAttendanceColor(stop.displayStatus)}`,
                    marginBottom: "10px",
                    backgroundColor:
                      stop.displayStatus === "picked_up"
                        ? "#f1f8f4"
                        : stop.displayStatus === "dropped_off"
                          ? "#e8f4fd"
                          : stop.displayStatus === "cancelled" ||
                              stop.displayStatus === "absent"
                            ? "#fff5f5"
                            : "#fff9f0",
                    borderRadius: "6px",
                    transition: "all 0.3s ease",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontWeight: "600",
                          fontSize: "14px",
                          marginBottom: "4px",
                        }}
                      >
                        {stop.student_name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#888" }}>
                        {stop.parent_name}
                      </div>
                      <div style={{ fontSize: "11px", color: "#aaa" }}>
                        {stop.address?.length > 35
                          ? stop.address.slice(0, 35) + "..."
                          : stop.address}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: "11px",
                        padding: "5px 10px",
                        borderRadius: "12px",
                        backgroundColor: getAttendanceColor(stop.displayStatus),
                        color: "white",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {getAttendanceLabel(stop.displayStatus)}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Modal Map */}
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={13}
              options={{
                zoomControl: true,
                streetViewControl: true,
                mapTypeControl: false,
                fullscreenControl: true,
              }}
            >
              {renderMapMarkers()}
            </GoogleMap>
          </div>
        </Modal.Body>
      </Modal>

      {/* Inline Map */}
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={mapCenter}
        zoom={13}
        options={{
          zoomControl: false,
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {renderMapMarkers()}
      </GoogleMap>

      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(76, 175, 80, 0); }
          100% { box-shadow: 0 0 0 0 rgba(76, 175, 80, 0); }
        }
        @keyframes ripple {
          0% { transform: scale(1); opacity: 0.4; }
          100% { transform: scale(1.5); opacity: 0; }
        }
      `}</style>
    </div>
  );
};

export default SchoolBusTracker;
